// src/components/EquipmentManager/ReviewsTab.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaStar, FaFilter } from 'react-icons/fa';
import RatingBreakdown from './RatingBreakdown';
import ReviewCard from './ReviewCard';
import WriteReviewModal from './WriteReviewModal';
import { SkeletonReview } from '../common/LoadingSkeleton';
import { useNotification } from '../../context/NotificationContext';
import useReviews from '../../hooks/useReviews';
import styles from './ReviewsTab.module.css';

const ReviewsTab = ({ 
  equipment, 
  currentUserId,
  canManage = false
}) => {
  const { showSuccess, showError } = useNotification();
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [filterRating, setFilterRating] = useState('');
  const [userAssignment, setUserAssignment] = useState(null);
  const [userExistingReview, setUserExistingReview] = useState(null);
  
  // Fetch reviews with current sort/filter options
  const {
    reviews,
    summary,
    loading,
    error,
    refresh,
    submitReview,
    updateReview,
    deleteReview,
    voteOnReview
  } = useReviews(equipment._id, { sort: sortBy, rating: filterRating });
  
  // Check if user was assigned this equipment
  useEffect(() => {
    const checkUserAssignment = async () => {
      if (!currentUserId) return;
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/my-assignments`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          // Check if user has a returned assignment for this equipment
          const assignment = [...(data.history || []), ...(data.active || [])]
            .find(a => {
              const eqId = a.equipmentId?._id || a.equipmentId;
              return String(eqId) === String(equipment._id);
            });
          
          setUserAssignment(assignment);
        }
      } catch (error) {
        console.error('Error checking user assignment:', error);
      }
    };
    
    checkUserAssignment();
  }, [currentUserId, equipment._id]);
  
  // Check if user has already reviewed this equipment
  useEffect(() => {
    if (!currentUserId) return;
    
    const existingReview = reviews.find(r => {
      const reviewUserId = r.userId?._id || r.userId;
      return String(reviewUserId) === String(currentUserId);
    });
    
    setUserExistingReview(existingReview);
  }, [reviews, currentUserId]);
  
  const handleWriteReview = () => {
    setShowWriteReview(true);
    setEditingReview(null);
  };
  
  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowWriteReview(true);
  };
  
  const handleSubmitReview = async (reviewData) => {
    try {
      if (editingReview) {
        await updateReview(editingReview._id, reviewData);
      } else {
        await submitReview(reviewData);
      }
      refresh();
    } catch (error) {
      throw error;
    }
  };
  
  const handleDeleteReview = async (reviewId) => {
    try {
      await deleteReview(reviewId);
      showSuccess('Review deleted successfully');
      refresh();
    } catch (error) {
      showError('Failed to delete review');
      throw error;
    }
  };
  
  const handleVote = async (reviewId, vote) => {
    try {
      await voteOnReview(reviewId, vote);
    } catch (error) {
      showError('Failed to register vote');
      throw error;
    }
  };
  
  const canWriteReview = currentUserId && !userExistingReview;
  
  return (
    <div className={styles.reviewsTab}>
      {/* Rating Summary */}
      <div className={styles.ratingSummarySection}>
        <RatingBreakdown ratingSummary={equipment.ratingSummary || summary} />
        
        {/* Write Review Button */}
        {canWriteReview && (
          <button 
            onClick={handleWriteReview}
            className={styles.writeReviewBtn}
          >
            <FaStar /> Write a Review
          </button>
        )}
        
        {userExistingReview && (
          <div className={styles.reviewStatus}>
            <FaStar className={styles.statusIcon} />
            You've already reviewed this equipment
            <button 
              onClick={() => handleEditReview(userExistingReview)}
              className={styles.editReviewBtn}
            >
              Edit Your Review
            </button>
          </div>
        )}
      </div>
      
      {/* Filters & Sort */}
      <div className={styles.reviewFilters}>
        <div className={styles.filterLeft}>
          <FaFilter className={styles.filterIcon} />
          <span className={styles.filterLabel}>
            {reviews.length} Review{reviews.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className={styles.filterRight}>
          <select 
            value={filterRating} 
            onChange={(e) => setFilterRating(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Ratings</option>
            <option value="5">5 Stars Only</option>
            <option value="4">4 Stars Only</option>
            <option value="3">3 Stars Only</option>
            <option value="2">2 Stars Only</option>
            <option value="1">1 Star Only</option>
          </select>
          
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="recent">Most Recent</option>
            <option value="helpful">Most Helpful</option>
            <option value="rating_high">Highest Rated</option>
            <option value="rating_low">Lowest Rated</option>
            <option value="verified">Verified First</option>
          </select>
        </div>
      </div>
      
      {/* Reviews List */}
      <div className={styles.reviewsList}>
        {loading ? (
          <>
            {[1, 2, 3].map(i => (
              <SkeletonReview key={i} />
            ))}
          </>
        ) : error ? (
          <div className={styles.error}>
            <p>Error loading reviews: {error}</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className={styles.noReviews}>
            <FaStar className={styles.noReviewsIcon} />
            <p>No reviews yet</p>
            {canWriteReview && (
              <button onClick={handleWriteReview} className={styles.beFirstBtn}>
                Be the first to review!
              </button>
            )}
          </div>
        ) : (
          reviews.map(review => (
            <ReviewCard 
              key={review._id} 
              review={review}
              currentUserId={currentUserId}
              canManage={canManage}
              onVote={handleVote}
              onDelete={handleDeleteReview}
              onEdit={handleEditReview}
            />
          ))
        )}
      </div>
      
      {/* Write/Edit Review Modal */}
      {showWriteReview && (
        <WriteReviewModal
          isOpen={showWriteReview}
          equipment={equipment}
          onClose={() => {
            setShowWriteReview(false);
            setEditingReview(null);
          }}
          onSubmit={handleSubmitReview}
          editMode={!!editingReview}
          existingReview={editingReview}
        />
      )}
    </div>
  );
};

ReviewsTab.propTypes = {
  equipment: PropTypes.object.isRequired,
  currentUserId: PropTypes.string,
  canManage: PropTypes.bool
};

export default ReviewsTab;

